/**
 * PORTED — RO-11-03 / #44588.
 * Origin: `excellent/app/_chrome/section-rail.tsx`.
 *
 * Changes: next/link + next/navigation → @excellent/ui/router (RouterLink,
 * usePathname, useSearch). Copied into client/shell/chrome/ — never import
 * from app/ (PRD D6).
 */

import { RouterLink as Link } from "@excellent/ui/router";
import {
  useEffect,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import {
  cn,
  useRailExtension,
  type RailExtension,
  type RailExtensionLink,
  type RailExtensionTocGroup,
} from "@excellent/ui";

interface SectionRailProps {
  /**
   * Rail chrome pinned above the scrolling nav body — the workspace + project
   * switchers (see rail-chrome.tsx). Prepared on the server and handed in by
   * the root layout.
   */
  topSlot?: ReactNode;
}

/**
 * The left navigation rail. A fixed-width column rendered only when a surface
 * pushes a LEFT rail extension (the docs viewer's sibling list + on-this-page
 * TOC). Settings AND Database both moved their nav to the right-gutter
 * ContextRail (see app/settings/_rail.tsx + app/database/_rail.tsx), so neither
 * renders here any more — this rail now carries only the default (extension-only)
 * body.
 */
export function SectionRail({
  topSlot,
}: SectionRailProps) {
  return (
    <aside className="flex h-full w-56 shrink-0 flex-col">
      {topSlot}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-4 pb-2">
        <DefaultRailBody />
      </div>
    </aside>
  );
}

function RailExtensionSections({ extension }: { extension: RailExtension }) {
  return (
    <>
      <div className="mt-5">
        <div className="px-2.5 pb-1.5 type-subtext text-faint">
          {extension.groupLabel}
        </div>
        <div className="space-y-px">
          {extension.links.map((link) => (
            <ExtensionLinkRow
              // Key on href+label, not href alone: ungenerated ("new") siblings
              // all point at the same fallback href, so href alone collides.
              key={`${link.href}|${link.label}`}
              link={link}
              active={link.href === extension.activeHref}
            />
          ))}
        </div>
      </div>

      {extension.toc.length > 0 ? (
        <div className="mt-5">
          <div className="px-2.5 pb-1.5 type-subtext text-faint">
            On this page
          </div>
          <RailToc groups={extension.toc} />
        </div>
      ) : null}
    </>
  );
}

function ExtensionLinkRow({
  link,
  active,
}: {
  link: RailExtensionLink;
  active: boolean;
}) {
  return (
    <Link
      href={link.href}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 type-body tracking-tight transition-colors",
        active
          ? "nav-active text-foreground"
          : link.dim
            ? "text-faint hover:text-foreground hover:bg-secondary/60"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
      )}
    >
      {/* No icons in the nav rail — EVER (Matt, 2026-07-23: "we don't want icons
          in that nav ever at all; indicators and numbers are fine"). `link.icon`
          is intentionally not rendered; the label + badge/indicator carry it. */}
      <span className="truncate" title={link.label}>
        {link.label}
      </span>
      {link.badge ? (
        <span className="ml-auto type-subtext uppercase tracking-[0.08em] text-faint">
          {link.badge}
        </span>
      ) : null}
    </Link>
  );
}

function RailToc({
  groups,
}: {
  groups: RailExtensionTocGroup[];
}) {
  const anchors = groups.flatMap((g) => [g.anchor, ...g.children.map((c) => c.anchor)]);
  const active = useActiveHeading(anchors);

  function handleClick(e: MouseEvent<HTMLAnchorElement>, anchor: string) {
    const el = document.getElementById(anchor);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${anchor}`);
  }

  return (
    <div className="space-y-px">
      {groups.map((group) => {
        const parentActive = group.anchor === active;
        const childActive = group.children.some((c) => c.anchor === active);
        const expanded = parentActive || childActive;
        return (
          <div key={group.anchor}>
            <a
              href={`#${group.anchor}`}
              onClick={(e) => handleClick(e, group.anchor)}
              className={cn(
                "block rounded-md px-2.5 py-1 type-body tracking-tight transition-colors",
                parentActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
              )}
            >
              <span className="truncate" title={group.text}>
                {group.text}
              </span>
            </a>
            {expanded && group.children.length > 0 ? (
              <div className="space-y-px">
                {group.children.map((child) => (
                  <a
                    key={child.anchor}
                    href={`#${child.anchor}`}
                    onClick={(e) => handleClick(e, child.anchor)}
                    className={cn(
                      "block rounded-md py-1 pl-7 pr-2.5 type-body tracking-tight transition-colors",
                      child.anchor === active
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                    )}
                  >
                    <span className="truncate" title={child.text}>
                      {child.text}
                    </span>
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let cur = el?.parentElement ?? null;
  while (cur && cur !== document.body) {
    const style = window.getComputedStyle(cur);
    if (/(auto|scroll)/.test(style.overflowY)) return cur;
    cur = cur.parentElement;
  }
  return null;
}

export function useActiveHeading(anchors: string[]): string | null {
  const key = anchors.join("|");
  const [active, setActive] = useState<string | null>(anchors[0] ?? null);

  useEffect(() => {
    if (anchors.length === 0) return;
    const els = anchors
      .map((a) => document.getElementById(a))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const root = findScrollParent(els[0] ?? null);
    const scrollEl: HTMLElement | Window = root ?? window;

    let raf = 0;
    function compute() {
      raf = 0;
      const rootTop = root ? root.getBoundingClientRect().top : 0;
      const rootHeight = root ? root.clientHeight : window.innerHeight;
      const threshold = rootTop + rootHeight * 0.25;
      let current: string | null = anchors[0] ?? null;
      for (const el of els) {
        if (el.getBoundingClientRect().top <= threshold) current = el.id;
        else break;
      }
      setActive(current);
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    }

    compute();
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}

/**
 * The fallback rail body — now extension-only. The core surfaces
 * (Home/Issues/Planner/Agents) moved to the top-nav Tools switcher and the
 * business-function sections to the Department switcher, so in the main app this
 * body is empty and the whole left column is gated away (see left-rail.tsx). It
 * still renders the rail extension a surface pushes — e.g. the docs viewer's
 * sibling list + on-this-page TOC — which is the one default-context reason the
 * rail appears at all.
 */
function DefaultRailBody() {
  const extension = useRailExtension();
  // Right-side extensions render in the right gutter well (ContextRail), never here.
  return extension && extension.side !== "right" ? (
    <RailExtensionSections extension={extension} />
  ) : null;
}

